import React from "react";
import { getReportsRepository } from "./repository/getReportsRepository.ts";
import { ReportSummary } from "./repository/ReportsRepository";
import "./ReportsScreen.css";

export function ReportsScreen() {
  const repo = React.useMemo(() => getReportsRepository(), []);
  const [data, setData] = React.useState<ReportSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [openingId, setOpeningId] = React.useState<string | null>(null);
  const [projectFilter, setProjectFilter] = React.useState("");
  const [inspectionFilter, setInspectionFilter] = React.useState("");

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await repo.listReports();
        if (!mounted) return;
        setData(res);
        setError(null);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? "Error");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [repo]);

  const openReport = async (report: ReportSummary) => {
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Opening report...";
      pdfWindow.document.body.innerHTML =
        '<p style="font-family: sans-serif; padding: 24px;">Opening report...</p>';
    }

    try {
      setOpeningId(report.id);
      setError(null);
      const blob = await repo.downloadReportPdf(report.id);
      const url = URL.createObjectURL(blob);

      if (pdfWindow) {
        pdfWindow.location.replace(url);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download = `${report.title || "report"}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      pdfWindow?.close();
      setError(e?.message ?? "Could not open the report.");
    } finally {
      setOpeningId(null);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const formatStatus = (status: ReportSummary["status"]) =>
    status.charAt(0).toUpperCase() + status.slice(1);

  const projects = React.useMemo(
    () =>
      Array.from(
        new Set(data.map((report) => report.projectName).filter(Boolean) as string[]),
      ).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  const inspections = React.useMemo(
    () =>
      Array.from(
        new Set(
          data
            .filter(
              (report) =>
                !projectFilter || report.projectName === projectFilter,
            )
            .map((report) => report.inspectionName)
            .filter(Boolean) as string[],
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [data, projectFilter],
  );

  const filteredReports = React.useMemo(
    () =>
      data.filter(
        (report) =>
          (!projectFilter || report.projectName === projectFilter) &&
          (!inspectionFilter || report.inspectionName === inspectionFilter),
      ),
    [data, projectFilter, inspectionFilter],
  );

  const reportStats = React.useMemo(
    () => ({
      total: data.length,
      ready: data.filter((report) => report.status === "ready").length,
      images: data.reduce((sum, report) => sum + (report.totalImages || 0), 0),
      detections: data.reduce((sum, report) => sum + (report.totalDetections || 0), 0),
    }),
    [data],
  );

  if (loading) {
    return (
      <div className="reports-page reports-page--center">
        <div className="reports-loading-card">
          <div className="reports-spinner" />
          <span>Loading reports...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      <header className="reports-header">
        <div>
          <p className="reports-eyebrow">Generated documents</p>
          <h1>Reports</h1>
          <p className="reports-subtitle">
            Review generated PDFs, filter by project or inspection, and open the original report.
          </p>
        </div>
      </header>

      {error && (
        <div className="reports-alert" role="alert">
          {error}
        </div>
      )}

      {!data.length && (
        <div className="reports-empty">
          <div className="reports-empty-icon">PDF</div>
          <h2>No reports generated yet</h2>
          <p>
            Reports will appear here after you generate a PDF from an analyzed inspection.
          </p>
        </div>
      )}

      {data.length > 0 && (
        <>
          <section className="reports-metrics" aria-label="Report metrics">
            <div className="reports-metric-card">
              <span>Total reports</span>
              <strong>{reportStats.total}</strong>
            </div>
            <div className="reports-metric-card">
              <span>Ready</span>
              <strong>{reportStats.ready}</strong>
            </div>
            <div className="reports-metric-card">
              <span>Images</span>
              <strong>{reportStats.images}</strong>
            </div>
            <div className="reports-metric-card">
              <span>Detections</span>
              <strong>{reportStats.detections}</strong>
            </div>
          </section>

          <section className="reports-toolbar" aria-label="Report filters">
            <label className="reports-field">
              <span>Project</span>
              <select
                value={projectFilter}
                onChange={(event) => {
                  setProjectFilter(event.target.value);
                  setInspectionFilter("");
                }}
              >
                <option value="">All projects</option>
                {projects.map((project) => (
                  <option key={project} value={project}>
                    {project}
                  </option>
                ))}
              </select>
            </label>

            <label className="reports-field">
              <span>Inspection</span>
              <select
                value={inspectionFilter}
                onChange={(event) => setInspectionFilter(event.target.value)}
              >
                <option value="">All inspections</option>
                {inspections.map((inspection) => (
                  <option key={inspection} value={inspection}>
                    {inspection}
                  </option>
                ))}
              </select>
            </label>

            {(projectFilter || inspectionFilter) && (
              <button
                className="reports-clear-btn"
                type="button"
                onClick={() => {
                  setProjectFilter("");
                  setInspectionFilter("");
                }}
              >
                Clear filters
              </button>
            )}
          </section>
        </>
      )}

      {data.length > 0 && filteredReports.length === 0 ? (
        <div className="reports-empty reports-empty--small">
          <h2>No reports match these filters</h2>
          <p>Try another project or inspection to broaden the list.</p>
        </div>
      ) : (
        filteredReports.length > 0 && (
          <section className="reports-list" aria-label="Reports list">
            <div className="reports-list-head">
              <span>Report</span>
              <span>Project</span>
              <span>Inspection</span>
              <span>Stats</span>
              <span>Status</span>
              <span />
            </div>

            {filteredReports.map((r) => (
              <article key={r.id} className="reports-row">
                <div className="reports-title-cell">
                  <div className="reports-pdf-icon">PDF</div>
                  <div>
                    <h2>{r.title}</h2>
                    <p>{formatDate(r.date)}</p>
                  </div>
                </div>
                <div className="reports-muted-cell">
                  {r.projectName || "No project"}
                </div>
                <div className="reports-muted-cell">
                  {r.inspectionName || "No inspection"}
                </div>
                <div className="reports-stats-cell">
                  <span>{r.totalImages} images</span>
                  <span>{r.totalDetections} detections</span>
                </div>
                <div>
                  <span className={`reports-status reports-status--${r.status}`}>
                    {formatStatus(r.status)}
                  </span>
                </div>
                <div className="reports-action-cell">
                  <button
                    type="button"
                    className="reports-open-btn"
                    onClick={() => openReport(r)}
                    disabled={openingId === r.id}
                  >
                    {openingId === r.id ? "Opening..." : "Open PDF"}
                  </button>
                </div>
              </article>
            ))}
          </section>
        )
      )}
    </div>
  );
}
